import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Loader2, MapPin } from "lucide-react";
import { SQFT_OPTIONS } from "./useBookingForm";

const fieldLabel = "text-xs font-mono uppercase tracking-[0.15em] text-slate-700 font-medium";
const inputCls = "mt-2 rounded-none border-slate-300";

/** Step 1 — property address, square footage, and an optional coupon code. */
export function PropertyStep({ form }) {
  const { address, setAddress, lookupSqft, sqft, setSqft, sqftLoading, sqftAuto, setSqftAuto, blocks, couponCode, setCouponCode, appliedCoupon, applyCoupon } = form;
  return (
    <div data-testid="step-property" className="space-y-6 fade-up">
      <h2 className="font-display text-3xl tracking-tight flex items-center gap-2"><MapPin className="h-6 w-6" strokeWidth={1.5} /> Property address</h2>
      <div>
        <Label className={fieldLabel}>Street address</Label>
        <Input data-testid="address-street" required value={address.street} onChange={(e) => setAddress({ ...address, street: e.target.value })} onBlur={lookupSqft} className={inputCls} placeholder="123 Ocean Ave" autoComplete="street-address" />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div><Label className={fieldLabel}>City</Label><Input data-testid="address-city" required value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} onBlur={lookupSqft} className={inputCls} /></div>
        <div><Label className={fieldLabel}>State</Label><Input data-testid="address-state" required value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} onBlur={lookupSqft} className={inputCls} /></div>
        <div><Label className={fieldLabel}>ZIP</Label><Input data-testid="address-zip" required value={address.zip} onChange={(e) => setAddress({ ...address, zip: e.target.value })} onBlur={lookupSqft} className={inputCls} /></div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className={fieldLabel}>Square footage</Label>
          {sqftLoading && <span className="text-[11px] font-mono text-slate-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> fetching…</span>}
          {!sqftLoading && sqftAuto && <span className="text-[11px] font-mono text-emerald-700">auto-selected · editable</span>}
        </div>
        <Select value={sqft || ""} onValueChange={(v) => { setSqft(v); setSqftAuto(false); }}>
          <SelectTrigger data-testid="address-sqft" className={inputCls}><SelectValue placeholder="Select square footage" /></SelectTrigger>
          <SelectContent>
            {SQFT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`sqft-opt-${o.value}`}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500 mt-2">Pricing is tiered by square footage{sqft ? ` · tier T${blocks + 1}` : ""}.</p>
      </div>
      <div className="border border-slate-300 p-5 bg-secondary">
        <Label className={fieldLabel}>Coupon code (optional)</Label>
        <div className="flex gap-3 mt-2">
          <Input data-testid="coupon-input" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} className="rounded-none bg-card border-slate-300" placeholder="Enter code" />
          <Button data-testid="coupon-apply" type="button" variant="outline" className="rounded-none border-slate-400" onClick={applyCoupon}>Apply</Button>
        </div>
        {appliedCoupon && <p data-testid="coupon-applied" className="text-sm text-emerald-700 mt-2 flex items-center gap-1"><Check className="h-4 w-4" /> {appliedCoupon.code} — {appliedCoupon.type === "percent" ? `${appliedCoupon.value}% off` : `$${appliedCoupon.value} off`}</p>}
      </div>
    </div>
  );
}
